import { Button } from "@/components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import * as Sentry from "@sentry/react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useCountdown } from "usehooks-ts";

const RELOAD_KEY = "dash-dom-crash-reload-at";
const RELOAD_COOLDOWN_MS = 10_000;
const RELOAD_TIMEOUT_SECONDS = 5;

const isDomCommitError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("removeChild") &&
    message.includes("not a child of this node")
  );
};

const isOutsideReloadCooldown = (): boolean => {
  const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? "0");
  return Date.now() - lastReloadAt > RELOAD_COOLDOWN_MS;
};

export const RouterErrorComponent = ({ error }: ErrorComponentProps) => {
  const [willHardReload] = useState(
    () => isDomCommitError(error) && isOutsideReloadCooldown(),
  );
  const [shouldCountdown, setShouldCountdown] = useState(true);
  const [countdown, { startCountdown, stopCountdown }] = useCountdown({
    countStart: RELOAD_TIMEOUT_SECONDS,
  });

  useEffect(() => {
    if (!willHardReload) return;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    void Sentry.flush(2000).finally(() => window.location.reload());
  }, [willHardReload]);

  useEffect(() => {
    if (willHardReload) return;
    startCountdown();
  }, [willHardReload, startCountdown]);

  useEffect(() => {
    if (willHardReload || !shouldCountdown) return;
    if (countdown === 0) {
      window.location.reload();
    }
  }, [countdown, shouldCountdown, willHardReload]);

  if (willHardReload) return null;

  const stopCounter = () => {
    stopCountdown();
    setShouldCountdown(false);
  };

  const message = error instanceof Error ? error.message : String(error);

  return (
    <div
      className="bg-background flex h-full flex-col items-center justify-center p-4"
      onClick={stopCounter}
    >
      <div className="flex max-w-lg flex-col gap-4">
        <div className="text-4xl">Something went wrong</div>
        <div className="text-foreground-5 text-sm">{message}</div>
        <Button
          onClick={(event) => {
            event.stopPropagation();
            window.location.reload();
          }}
        >
          <FontAwesomeIcon icon={faRotateRight} />
          {shouldCountdown ? (
            <>Reloading in {countdown} seconds...</>
          ) : (
            <>Reload now</>
          )}
        </Button>
      </div>
    </div>
  );
};
